import React, { isValidElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import NavBar from './NavBar';
import Headline from './Headline';
import SubHeadline from './SubHeadline';
import Breadcrumb from './Breadcrumb';

/**
 * Horizontal navigation component
 *
 * @param {Object} props Props
 * @param {Object[]} props.navigation Navigation data
 * @param {HeadingData} props.navHeading Heading data
 * @param {SubHeadlineData} props.navSubHeadline subheadline data
 * @param {BreadcrumbData} props.navBreadcrumbData BreadcrumbData data
 * @param {HeadingLeftData} props.afterNavHeadingLeft Heading Left data
 * @param {HeadingRightData} props.afterNavHeadingRight Heading Right data
 * @param {Boolean} props.withInPageNavContainer Whether to render in page nav container class
 * @returns {React.ReactElement} Rendered navigation row
 */
// eslint-disable-next-line complexity
function InPageNav({
  navigation = [],
  navHeading = {},
  navSubHeadline = {},
  navBreadcrumb = {},
  afterNavHeadingLeft,
  beforeNavHeading,
  afterNavHeadingRight,
  inPageNavContainerClass
}) {
  let navOptions = navigation;

  if (!Array.isArray(navOptions)) {
    navOptions = [];
  }

  const navBreadcrumbExists = navBreadcrumb && navBreadcrumb.href && navBreadcrumb.text;
  const navHeadingExists = navHeading && navHeading.text;
  const navSubHeadlineExists = navSubHeadline && (navSubHeadline.text || isValidElement(navSubHeadline));
  const navHeadingPlacement = navHeadingExists ? 'nav-heading-adjustment' : '';
  const hasInPageNav = navOptions.length > 0 || navHeadingExists || navBreadcrumbExists;

  const navContent = hasInPageNav ? (
    <>
      { navBreadcrumbExists && <Breadcrumb
        href={ navBreadcrumb.href }
        text={ navBreadcrumb.text }
        eid={ navBreadcrumb.eid }
        onClick={ navBreadcrumb.onClick }/> }
      { navHeadingExists && (
        <div
          data-testid='nav-headline'
          className={ classNames('in-page-nav-headline', {
            'with-nav': navOptions.length > 0,
            'with-heading-right': !!afterNavHeadingRight,
            'with-before-nav-heading': !!beforeNavHeading }) }
        >
          <div className='nav-heading-left'>
            { beforeNavHeading }
            <div data-testid='nav-heading-subheading-lockup' className='nav-heading-subheading-lockup'>
              <Headline { ...navHeading } afterNavHeadingLeft={ afterNavHeadingLeft } />
              { navSubHeadlineExists && <SubHeadline navSubHeadline={ navSubHeadline } /> }
            </div>
          </div>
          { afterNavHeadingRight }
        </div>
      )}
      <div data-testid='in-page-nav-bar-wrapper' key='desktop-nav' className={ `${navHeadingPlacement}` }>
        <NavBar items={ navOptions }/>
      </div>
    </>
  ) : (
    <div data-testid='nav-header-empty' className='hcs-missing-sub-nav-spacing' />
  );

  return (
    <div data-testid='in-page-nav-container' className={ classNames('horizontal-navigation', inPageNavContainerClass) }>
      { navContent }
    </div>
  );
}

InPageNav.propTypes = {
  navigation: PropTypes.arrayOf(PropTypes.object),
  navHeading: PropTypes.shape({
    text: PropTypes.string
  }),
  navSubHeadline: PropTypes.oneOfType([
    PropTypes.shape({
      text: PropTypes.string
    }),
    PropTypes.node
  ]),
  navBreadcrumb: PropTypes.shape({
    href: PropTypes.string,
    text: PropTypes.string,
    eid: PropTypes.string,
    onClick: PropTypes.func
  }),
  afterNavHeadingLeft: PropTypes.node,
  beforeNavHeading: PropTypes.node,
  afterNavHeadingRight: PropTypes.node,
  inPageNavContainerClass: PropTypes.string
};

export default InPageNav;
